<template>
  <header class="header-modern">
    <nav class="container mx-auto px-4 py-1.5">
      <div class="flex items-center justify-between">
        <!-- Logo with glow effect -->
        <router-link to="/" class="logo-container">
          <div class="logo-glow"></div>
          <img
            src="/logo-no-bg.png"
            alt="BlogDavid Logo"
            width="130"
            height="40"
            class="logo-image"
          />
        </router-link>

        <!-- Desktop Navigation -->
        <div class="hidden md:flex items-center space-x-6">
          <router-link
            to="/"
            class="nav-link"
            :class="{ 'nav-link-active': $route.name === 'Home' }"
          >
            <i class="bx bx-home text-lg"></i>
            <span>Início</span>
          </router-link>

          <!-- User authenticated -->
          <template v-if="authStore.isAuthenticated">
            <router-link
              to="/saved"
              class="nav-link"
              :class="{ 'nav-link-active': $route.name === 'SavedPosts' }"
            >
              <i class="bx bx-bookmark text-lg"></i>
              <span>Salvos</span>
            </router-link>

            <!-- Admin links -->
            <template v-if="authStore.isAdmin">
              <router-link
                to="/admin"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'AdminDashboard' }"
              >
                <i class="bx bx-cog text-lg"></i>
                <span>Admin</span>
              </router-link>
            </template>

            <!-- User menu -->
            <div class="relative" ref="userMenu">
              <button
                @click="toggleUserMenu"
                class="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-gray-50"
                data-tutorial="profile"
              >
                <div class="flex items-center space-x-1.5">
                  <img
                    v-if="profileImageUrl"
                    :src="profileImageUrl"
                    :alt="authStore.user.username"
                    class="w-7 h-7 rounded-full object-cover border-2 border-gray-200"
                  />
                  <div
                    v-else
                    class="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center border-2 border-primary-200"
                  >
                    <span class="text-primary-600 font-medium text-xs">
                      {{ authStore.user?.username?.charAt(0)?.toUpperCase() }}
                    </span>
                  </div>
                  <div class="text-left">
                    <div class="text-sm font-medium">
                      {{ authStore.user?.username }}
                    </div>
                    <div class="text-xs text-gray-500" v-if="authStore.isAdmin">
                      Admin
                    </div>
                  </div>
                </div>
                <i
                  class="bx bx-chevron-down text-base"
                  :class="{ 'rotate-180': showUserMenu }"
                ></i>
              </button>

              <!-- Dropdown menu -->
              <div
                v-show="showUserMenu"
                class="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50"
              >
                <router-link
                  to="/profile"
                  class="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors"
                  @click="closeUserMenu"
                >
                  <i class="bx bx-user text-lg"></i>
                  <span>Meu Perfil</span>
                </router-link>
                <hr class="my-2 border-gray-100" />
                <button
                  @click="handleLogout"
                  class="flex items-center space-x-3 w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors"
                >
                  <i class="bx bx-log-out text-lg"></i>
                  <span>Sair</span>
                </button>
              </div>
            </div>
          </template>

          <!-- Guest -->
          <template v-else>
            <div class="flex items-center space-x-2">
              <router-link
                to="/login"
                class="flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-primary-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <i class="bx bx-log-in text-base"></i>
                <span>Entrar</span>
              </router-link>
              <router-link
                to="/register"
                class="flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              >
                <i class="bx bx-user-plus text-base"></i>
                <span>Cadastrar</span>
              </router-link>
            </div>
          </template>
        </div>

        <!-- Mobile menu button -->
        <button
          @click="toggleMobileMenu"
          class="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <i
            class="bx text-xl"
            :class="showMobileMenu ? 'bx-x' : 'bx-menu'"
          ></i>
        </button>
      </div>

      <!-- Mobile Navigation -->
      <div
        v-show="showMobileMenu"
        class="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4"
      >
        <div class="flex flex-col space-y-2">
          <router-link
            to="/"
            class="flex items-center space-x-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors px-3 py-3 rounded-lg"
            @click="closeMobileMenu"
          >
            <i class="bx bx-home text-lg"></i>
            <span>Início</span>
          </router-link>

          <!-- User authenticated -->
          <template v-if="authStore.isAuthenticated">
            <router-link
              to="/saved"
              class="flex items-center space-x-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors px-3 py-3 rounded-lg"
              @click="closeMobileMenu"
            >
              <i class="bx bx-bookmark text-lg"></i>
              <span>Posts Salvos</span>
            </router-link>

            <router-link
              to="/profile"
              class="flex items-center space-x-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors px-3 py-3 rounded-lg"
              @click="closeMobileMenu"
            >
              <i class="bx bx-user text-lg"></i>
              <span>Meu Perfil</span>
            </router-link>

            <!-- Admin links -->
            <template v-if="authStore.isAdmin">
              <router-link
                to="/admin"
                class="flex items-center space-x-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors px-3 py-3 rounded-lg"
                @click="closeMobileMenu"
              >
                <i class="bx bx-cog text-lg"></i>
                <span>Painel Admin</span>
              </router-link>
            </template>

            <hr class="my-3 border-gray-200" />
            <button
              @click="handleLogout"
              class="flex items-center space-x-3 w-full text-left text-gray-700 hover:text-red-600 hover:bg-gray-50 transition-colors px-3 py-3 rounded-lg"
            >
              <i class="bx bx-log-out text-lg"></i>
              <span>Sair</span>
            </button>
          </template>

          <!-- Guest -->
          <template v-else>
            <div class="flex flex-col space-y-3 pt-2">
              <router-link
                to="/login"
                class="flex items-center justify-center space-x-2 btn-secondary w-full"
                @click="closeMobileMenu"
              >
                <i class="bx bx-log-in text-lg"></i>
                <span>Entrar</span>
              </router-link>
              <router-link
                to="/register"
                class="flex items-center justify-center space-x-2 btn-primary w-full"
                @click="closeMobileMenu"
              >
                <i class="bx bx-user-plus text-lg"></i>
                <span>Cadastrar</span>
              </router-link>
            </div>
          </template>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useAuthStore } from "@/store/auth";

export default {
  name: "AppHeader",
  setup() {
    const authStore = useAuthStore();

    // Computed para URL completa da imagem de perfil
    const profileImageUrl = computed(() => {
      const rawUrl = authStore.user?.profile_image_url;
      if (!rawUrl) return null;

      // Se for base64, valida antes de usar
      if (rawUrl.startsWith("data:image/")) {
        try {
          // Verifica se o base64 está completo (mínimo 100 caracteres após o cabeçalho)
          const base64Data = rawUrl.split(",")[1];
          if (!base64Data || base64Data.length < 100) {
            console.warn("⚠️ [Header] Base64 incompleto ou corrompido");
            return null;
          }
          return rawUrl;
        } catch (error) {
          console.error("❌ [Header] Erro ao validar base64:", error);
          return null;
        }
      }

      // Se já for uma URL completa (https/http), usa direto
      if (rawUrl.startsWith("http")) {
        return rawUrl;
      }

      // Se for uma URL relativa (/uploads/...), constrói com a base da API
      const apiBase =
        process.env.VUE_APP_API_URL || "http://localhost:3000/api";
      const baseUrl = apiBase.replace("/api", "");
      const fullUrl = rawUrl.startsWith("/")
        ? baseUrl + rawUrl
        : baseUrl + "/" + rawUrl;
      return fullUrl;
    });
    const showUserMenu = ref(false);
    const showMobileMenu = ref(false);
    const userMenu = ref(null);

    const toggleUserMenu = () => {
      showUserMenu.value = !showUserMenu.value;
    };

    const closeUserMenu = () => {
      showUserMenu.value = false;
    };

    const toggleMobileMenu = () => {
      showMobileMenu.value = !showMobileMenu.value;
    };

    const closeMobileMenu = () => {
      showMobileMenu.value = false;
    };

    const handleLogout = () => {
      authStore.logout();
      closeUserMenu();
      closeMobileMenu();
    };

    // Close menus when clicking outside
    const handleClickOutside = (event) => {
      if (userMenu.value && !userMenu.value.contains(event.target)) {
        closeUserMenu();
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      authStore,
      profileImageUrl,
      showUserMenu,
      showMobileMenu,
      userMenu,
      toggleUserMenu,
      closeUserMenu,
      toggleMobileMenu,
      closeMobileMenu,
      handleLogout,
    };
  },
};
</script>

<style scoped>
/* Modern Header with Glassmorphism */
.header-modern {
  @apply sticky top-0 z-40;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(229, 231, 235, 0.5);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -2px rgba(0, 0, 0, 0.05);
  animation: slideDown 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Logo with glow effect */
.logo-container {
  @apply relative block;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.logo-glow {
  @apply absolute inset-0 rounded-lg;
  background: radial-gradient(
    circle,
    rgba(59, 130, 246, 0.15) 0%,
    transparent 70%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  filter: blur(8px);
  z-index: -1;
}

.logo-container:hover .logo-glow {
  opacity: 1;
}

.logo-image {
  @apply transform transition-all duration-300;
}

.logo-container:hover .logo-image {
  transform: scale(1.05) translateY(-2px);
}

/* Modern Navigation Links */
.nav-link {
  @apply flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium;
  @apply text-gray-700 transition-all duration-300;
  position: relative;
  overflow: hidden;
}

.nav-link::before {
  content: "";
  @apply absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-primary-500 to-primary-600;
  transform: translateX(-100%);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.nav-link:hover {
  @apply text-primary-600 bg-primary-50/50;
  transform: translateY(-2px);
}

.nav-link:hover::before {
  transform: translateX(0);
}

.nav-link-active {
  @apply text-primary-600 font-semibold;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1) 0%,
    rgba(37, 99, 235, 0.05) 100%
  );
}

.nav-link-active::before {
  transform: translateX(0);
}

/* Icon animation on hover */
.nav-link:hover i {
  animation: iconBounce 0.5s ease;
}

@keyframes iconBounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

/* Scroll effect - add via JS */
.header-modern.scrolled {
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -4px rgba(0, 0, 0, 0.1);
}

/* Mobile menu animation */
@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Smooth transitions for all interactive elements */
.nav-link,
.logo-container {
  will-change: transform;
}
</style>
